import React from 'react';
import PropTypes from 'prop-types';
import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import AdjusterSelectTextFieldFormik from '~/Adjuster/AdjusterSelectTextFieldFormik';
import { useStyles } from '~/assets/styles';
import Button from '~/components/core/Atomic/Buttons/Button';
import Grid from '~/components/core/Atomic/Grid/Grid';
import Typography from '~/components/core/Atomic/Typography';
import CancelButton from '~/components/core/Buttons/CancelButton';
import RadioButtonGroupFormik from '~/components/core/Formik/RadioButtonGroupFormik';
import SubOrgMultiSelectWithChipsFormik from '~/components/core/Formik/SubOrgMultiSelectWithChipsFormik';
import SwitchFormik from '~/components/core/Formik/SwitchFormik';
import TooltipIcon from '~/components/core/TooltipIcon';
import { InfoIcon } from '~/components/icons';
import TextFieldFormik from '~/components/TextFieldFormik';
import { MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import { DATA_EXPORT } from '~/Types';

import CardDialog from '../../CardDialog';
import mixpanel from '../../CmsMain/mixpanel';
import AlertBanner from '../../core/AlertBanner';
import { useSysconfig } from '../SystemConfigurationScreen';

import { NON_CLAIM_COMMUNICATION_ATTENTION_NOTE } from './consts.json';

const OneTimeExportDialog = ({ onClose, onSubmit }) => {
  const { organization } = useSysconfig();
  return (
    <Formik
      initialValues={{
        sub_organization_ids: [],
        format: DATA_EXPORT.EXPORT_FORMAT.csv.key,
        user_id: '',
        file_name_prefix: '',
        include_non_claim_communications: false,
        include_email_communication_content: false,
        should_use_pgp_encryption: false,
        pgp_encryption_public_key: '',
      }}
      validationSchema={Yup.object().shape({
        sub_organization_ids: Yup.array().test('sub_orgs_ids', 'Required', (value) => {
          return organization.sub_organizations_enabled ? value?.length > 0 : true;
        }),
        format: Yup.string().required('Required'),
        user_id: Yup.string().required('Required'),
        should_use_pgp_encryption: Yup.boolean(),
        pgp_encryption_public_key: Yup.string()
          .nullable()
          .when('should_use_pgp_encryption', {
            is: true,
            then: Yup.string().nullable().required('PGP public key is required when encryption is enabled'),
          }),
      })}
      enableReinitialize
      onSubmit={async (values) => {
        if (values?.sub_organization_ids?.includes(SubOrgMultiSelectWithChipsFormik.ALL_OPTION.id)) {
          values.sub_organization_ids = [];
          values.is_all_sub_orgs = true;
        }
        if (!values.should_use_pgp_encryption) {
          values.pgp_encryption_public_key = undefined;
        }
        const submittedSuccessfully = await onSubmit(values);
        if (!submittedSuccessfully) {
          // reportAxiosError should have already been called in onSubmit
          return;
        }
        onClose();
        mixpanel.track(MIXPANEL_EVENTS.ONE_TIME_DATA_EXPORT_EMAIL_SUBMITTED, {
          sub_organization: values.sub_organization_id,
          format: values.format,
        });
      }}
    >
      <OneTimeExportDialogInner onClose={onClose} />
    </Formik>
  );
};

OneTimeExportDialog.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

const OneTimeExportDialogInner = ({ onClose }) => {
  const { organization, users } = useSysconfig();
  const filteredUsers = users.filter((user) => user.role.role_type !== 'system');
  const { classes } = useStyles();
  const { isSubmitting, handleSubmit, values } = useFormikContext();
  const { sub_organizations_enabled } = organization;

  return (
    <CardDialog
      isDialog
      title="One Time Email Export"
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      preventClose={isSubmitting}
    >
      <Grid container spacing={4} className="flex items-end">
        {sub_organizations_enabled && (
          <Grid item xs={6}>
            <Typography display="block" variant="subtitle1" className={classes.h4}>
              Sub Organizations
            </Typography>
            <SubOrgMultiSelectWithChipsFormik
              id="sub_organization_ids"
              label="Sub Organization"
              showOnly={isSubmitting}
              disabled={isSubmitting}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <Typography display="block" variant="subtitle1" className={classes.h4}>
            Format
          </Typography>
          <RadioButtonGroupFormik
            id="format"
            direction="row"
            options={
              Object.keys(DATA_EXPORT.EXPORT_FORMAT).map((key) => ({
                text: DATA_EXPORT.EXPORT_FORMAT[key].desc,
                optionValue: DATA_EXPORT.EXPORT_FORMAT[key].key,
              })) || []
            }
            showOnly={isSubmitting}
          />
        </Grid>
        <Grid item xs={6}>
          <Typography display="block" variant="subtitle1" className={classes.h4}>
            Email Address
          </Typography>
          <AdjusterSelectTextFieldFormik
            id="user_id"
            label="Select System User"
            className={classes.textField}
            fullWidth
            disabled={isSubmitting}
            sortAlphabetic
            allUsers={filteredUsers}
          />
        </Grid>
        <Grid item xs={12}>
          <div className="inline-flex items-baseline">
            <SwitchFormik
              id="include_non_claim_communications"
              label="Include communications not attached to a claim in DE files"
              disabled={isSubmitting}
            />
            <TooltipIcon
              placement="top-start"
              arrow
              title="Communications not attached to any claim may contain sub-organization specific information."
            >
              <InfoIcon className="ml-12" size={18} />
            </TooltipIcon>
          </div>
          <div className="inline-flex items-baseline">
            <SwitchFormik
              id="include_email_communication_content"
              label="Include email content in DE files"
              disabled={isSubmitting}
            />
            <TooltipIcon
              placement="top-start"
              arrow
              title="Includes the full email body in the exported EmailCommunication files. Email bodies may contain personal information. In Excel files, a body longer than 32,767 characters is cut and ends with '... [truncated]'."
            >
              <InfoIcon className="ml-12" size={18} />
            </TooltipIcon>
          </div>
          <AlertBanner
            title="Please Note"
            note={NON_CLAIM_COMMUNICATION_ATTENTION_NOTE}
            alertType={AlertBanner.ALERT_TYPES.INFO}
          />
        </Grid>
        <Grid item xs={12}>
          <SwitchFormik id="should_use_pgp_encryption" label="Encrypt export with PGP" disabled={isSubmitting} />
        </Grid>
        {values.should_use_pgp_encryption && (
          <Grid item xs={12}>
            <TextFieldFormik
              id="pgp_encryption_public_key"
              label="PGP Public Key"
              fullWidth
              multiline
              rows={6}
              className={classes.textField}
              disabled={isSubmitting}
              placeholder="-----BEGIN PGP PUBLIC KEY BLOCK-----"
            />
          </Grid>
        )}
        <div className={classes.buttonsContainer}>
          <CancelButton disabled={isSubmitting} onClick={onClose} />
          <Button variant="contained" color="primary" disabled={isSubmitting} onClick={() => handleSubmit()}>
            SEND
          </Button>
        </div>
      </Grid>
    </CardDialog>
  );
};

OneTimeExportDialogInner.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default OneTimeExportDialog;
